import { useMutation } from '@apollo/client';
import { TextField } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Radio from '@material-ui/core/Radio';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import gql from 'graphql-tag';
import React, { useState } from 'react';

const SAVE_REGISTRATION_MUTATION = gql`
  mutation CompRegistrationUpsert($input: CompRegistrationInput!) {
    compRegistrationUpsert(input: $input) {
      id
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    instructions: {
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2),
      backgroundColor: 'white',
      borderRadius: theme.shape.borderRadius,
      border: '1px solid rgba(0,0,0,0.06)',
      whiteSpace: 'pre-line',
      lineHeight: 1.6,
      fontSize: '1rem',
      color: '#333',
    },
    button: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1.5, 4),
      fontSize: '1rem',
      fontWeight: 600,
      borderRadius: theme.shape.borderRadius,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-1px)',
      },
      '&:disabled': {
        transform: 'none',
      },
    },
    divisionsContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
    },
    divisions: {
      maxWidth: 'max-content',
      width: '100%',
      backgroundColor: 'white',
      borderRadius: theme.shape.borderRadius,
      border: '1px solid rgba(0,0,0,0.06)',
      overflow: 'hidden',
    },
    listItem: {
      transition: 'all 0.2s ease-in-out',
      borderBottom: '1px solid rgba(0,0,0,0.06)',
      '&:last-child': {
        borderBottom: 'none',
      },
      '&:hover': {
        backgroundColor: '#f8f9fa',
        transform: 'translateX(4px)',
      },
      '&.Mui-selected': {
        backgroundColor: '#e3f2fd',
        '&:hover': {
          backgroundColor: '#bbdefb',
        },
      },
    },
    radio: {
      color: theme.palette.primary.main,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
    listItemText: {
      '& .MuiListItemText-primary': {
        fontWeight: 600,
        fontSize: '1rem',
        color: '#333',
      },
      '& .MuiListItemText-secondary': {
        fontSize: '0.875rem',
        color: '#666',
        marginTop: theme.spacing(0.5),
      },
    },
    extraFieldsSection: {
      marginTop: theme.spacing(3),
      width: '100%',
    },
    extraFieldsTitle: {
      marginBottom: theme.spacing(2),
      fontSize: '1rem',
      fontWeight: 600,
      color: '#333',
      textAlign: 'center',
    },
    extraFields: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(4),
      display: 'grid',
      gap: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: 'repeat(1,minmax(0,1fr))',
      },
      gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
        },
        '&.Mui-focused': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
          },
        },
      },
    },
  })
);

interface Props {
  track: any;
  currentUser: any;
  compRegistration: any;
  reload: () => Promise<any>;
}

const GENDERS = {
  M: 'Male',
  F: 'Female',
};

interface ExtraField {
  name: string;
  value: string;
  type: string;
  required: boolean;
}

const DivisionStep = ({
  track,
  currentUser,
  compRegistration,
  reload,
}: Props) => {
  const styles = useStyles();
  const [saving, setSaving] = useState(false);
  const [selectedValue, setSelectedValue] = useState();
  const [extraFields, setExtraFields] = useState<ExtraField[]>(
    track.compExtraFields
  );
  const [saveCompRegistrationMutation] = useMutation(
    SAVE_REGISTRATION_MUTATION
  );

  const hasDivisions =
    compRegistration.divisionOptions &&
    compRegistration.divisionOptions.length > 0;

  const saveCompRegistration = async () => {
    setSaving(true);
    try {
      const optionKey = hasDivisions ? 'division' : 'weightClass';
      await saveCompRegistrationMutation({
        variables: {
          input: {
            trackId: track.id,
            [optionKey]: selectedValue,

            extraFields: extraFields.length
              ? extraFields.map(({ value }) => ({ value }))
              : undefined,
          },
        },
      });
      await reload();
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  const options = hasDivisions
    ? compRegistration.divisionOptions
    : compRegistration.weightGroupOptions;

  const formIsValid =
    !!selectedValue &&
    extraFields.filter((f) => f.required).every((f) => !!f.value);

  return (
    <>
      <Box className={styles.instructions}>
        {hasDivisions ? (
          <>
            Gender category{' '}
            <strong>{GENDERS[compRegistration.gender] || 'Unknown'}</strong> is
          </>
        ) : (
          <>
            Age category <strong>{compRegistration.ageGroup.name}</strong> and
            gender category{' '}
            <strong>{GENDERS[compRegistration.gender] || 'Unknown'}</strong> are
          </>
        )}{' '}
        set by using your{' '}
        <strong>{currentUser.c2Username || currentUser.email}</strong> account
        information.
        {'\r\n'}
        Please choose the {hasDivisions ? 'division' : 'weight category'} you'll
        be competing in:
      </Box>
      <Box className={styles.divisionsContainer}>
        <List className={styles.divisions}>
          {options.map((option) => {
            const labelId = `option-${option.value}`;
            const isSelected = option.value === selectedValue;

            return (
              <ListItem
                key={option.value}
                role={undefined}
                dense
                button
                onClick={() => setSelectedValue(option.value)}
                className={`${styles.listItem} ${
                  isSelected ? 'Mui-selected' : ''
                }`}
              >
                <ListItemIcon>
                  <Radio
                    edge="start"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                    className={styles.radio}
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={option.name}
                  secondary={option.details}
                  className={styles.listItemText}
                />
              </ListItem>
            );
          })}
        </List>
        {extraFields.length > 0 && (
          <div className={styles.extraFieldsSection}>
            <div className={styles.extraFieldsTitle}>
              Please fill out some information required by the organizer:
            </div>
            <div className={styles.extraFields}>
              {extraFields.map((extraField, i) => {
                return (
                  <TextField
                    key={i}
                    name={extraField.name}
                    required={extraField.required}
                    variant="outlined"
                    label={extraField.name}
                    className={styles.textField}
                    onChange={(event) => {
                      const value = event.target.value;
                      setExtraFields((fields) => {
                        const newFields = [...fields];
                        newFields[i] = {
                          ...newFields[i],
                          value,
                        };
                        return newFields;
                      });
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </Box>
      <Button
        className={styles.button}
        disabled={!formIsValid || saving}
        variant="contained"
        color="primary"
        onClick={() => saveCompRegistration()}
      >
        {saving ? 'Continuing...' : 'Continue'}
      </Button>
    </>
  );
};

export default DivisionStep;
